import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';

// Crear una instancia base de axios con la URL de la API
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api',
  headers: {
    'Content-Type': 'application/json',
  },
  // Aumentar el tiempo de espera para el servidor de desarrollo
  timeout: 10000
});

// Modo simulado para desarrollo (false = deshabilita las respuestas simuladas cuando el servidor no está disponible)
const MOCK_MODE_FALLBACK = false;

// Variable para rastrear si hay un proceso de renovación de token en curso
let isRefreshing = false;
// Cola de solicitudes fallidas para reintentar después de renovar el token
let failedQueue: any[] = [];

// Procesar la cola de solicitudes fallidas después de renovar el token
const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Interceptor para agregar el token de autenticación a cada solicitud
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores comunes de respuesta y renovar tokens expirados
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Si no hay respuesta del servidor y el modo simulado está habilitado
    if (!error.response && MOCK_MODE_FALLBACK) {
      console.warn('Servidor no disponible, se permite continuar en modo simulado');
      // Permitir que la aplicación continúe, los servicios pueden manejar el modo simulado
      return Promise.reject(error);
    }
    
    const originalRequest = error.config;
    
    // Si el error es 401 (No autorizado) y no estamos intentando refrescar el token o hacer login
    if (
      error.response && 
      error.response.status === 401 && 
      !originalRequest._retry &&
      !originalRequest.url?.includes('auth/login') &&
      !originalRequest.url?.includes('auth/refresh')
    ) {
      if (isRefreshing) {
        // Si ya estamos refrescando el token, ponemos esta solicitud en cola
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        // Intentar refrescar el token
        const refreshToken = localStorage.getItem('refreshToken');
        
        if (!refreshToken) {
          // No hay token de actualización, redirigir a login
          window.location.href = '/login';
          return Promise.reject(error);
        }
        
        const response = await axios.post(
          `${api.defaults.baseURL}/auth/refresh/`,
          { refresh: refreshToken },
          { headers: { 'Content-Type': 'application/json' } }
        );
        
        // Guardar el nuevo token
        const { access } = response.data;
        localStorage.setItem('accessToken', access);
        
        // Procesar la cola de solicitudes pendientes
        processQueue(null, access);
        
        // Reintentar la solicitud original con el nuevo token
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Error al refrescar el token, limpiar tokens y redirigir a login
        processQueue(refreshError as AxiosError);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

export default api; 